import { Button } from '@/app/common/components/button/Button.tsx'
import { Input } from '@/app/common/components/input/Input.tsx'
import { Link, useNavigate } from 'react-router-dom'
import AceScholarshipsLogo from '@assets/AceScholarshipLogo1.jpg'
import { AppRoutePaths } from '../../../../routing/route_paths.ts'
import { AuthLayout } from '../layout.tsx'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  GETInvitationStatus,
  POSTResendInvitation,
  registerUser,
} from '@/apis/users/requests'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { EllipsesLoader } from '@/common/components/EllipsesLoader.tsx'
import { cn, formatPhoneNumber } from '@/common/utilities.ts'
import { store, persistor } from '@/app-store/store.ts'
import { global_error_codes } from '@/common/constants.ts'
import { EyeIcon, EyeSlashIcon } from '@/common/icons/general.tsx'
import { logout as legacyLogout } from '@/app-store/user/authSlice'

const UserRegistrationSchema = z
  .object({
    email: z.string().optional(),
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters long' }),
    confirm_password: z.string().min(8, {
      message: 'Confirm Password must be at least 8 characters long',
    }),
    phone: z.string().regex(/^\d{10}$|^\(\d{3}\) \d{3}-\d{4}$/, {
      message: 'Phone number must be exactly 10 digits long',
    }),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  })
type UserRegistrationFormProps = z.infer<typeof UserRegistrationSchema>

const SignUpPage = () => {
  const navigate = useNavigate()
  const { fireAlert } = useAppAlert()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const params = new URLSearchParams(location.search)
  const encodedQS = params.get('q')
  const [SearchData, setSearchData] = useState({
    user_id: 0,
    token: '',
    email: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const handleLogOut = () => {
    persistor.purge()
    /**
     * In Keycloak mode calling `keycloak.logout()` automatically redirects
     * to the login page defined during `keycloak.login()`. This redirect is
     * undesirable on `/signup` route after invitation identified. where we
     * only need to clear auth state without navigation. Therefore, we call
     * `legacyLogout()` here, which simply reset the Redux authentication
     * state (without redirecting).
     *
     * TODO Note:
     * - Ideally, a full Keycloak logout should occur after a successful
     *   `registerUser()` call.
     * - However, since the API currently returns a token decoding error,
     *   the post-registration flow could not be fully tested.
     */
    store.dispatch(legacyLogout())
  }

  const processInvitation = async () => {
    const response = await GETInvitationStatus(
      SearchData.email,
      SearchData.token
    )

    if (response.error) {
      if (response.error.code == global_error_codes.invitation_expired) {
        const userInput = await fireAlert({
          variant: 'error',
          confirmBtnText: 'Resend',
          denyBtn: true,
          denyBtnText: 'Cancel',
          swapButtons: true,
          message: response.error.message,
        })

        if (userInput.confirmed) {
          const callBackUrl =
            window.location.origin + AppRoutePaths.auth.sign_up
          const reSendResponse = await POSTResendInvitation({
            email: SearchData.email,
            invitation_token: SearchData.token,
            call_back_url: callBackUrl,
          })
          if (reSendResponse.error) {
            await fireAlert({
              variant: 'error',
              message: reSendResponse.error.message,
            })
          }

          navigate(AppRoutePaths.auth.login)
        }
      } else {
        await fireAlert({
          variant: 'error',
          message: response.error.message,
        })
        navigate(AppRoutePaths.auth.login)
      }
    }
  }

  useEffect(() => {
    if (SearchData.token) {
      processInvitation().then()
    }
  }, [SearchData])

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/\D/g, '')
    const formattedPhoneNumber = formatPhoneNumber(rawValue)
    setValue('phone', formattedPhoneNumber || rawValue, {
      shouldValidate: true,
    })
  }

  useEffect(() => {
    if (encodedQS) {
      try {
        const decodedQS = atob(encodedQS)
        const decodedParams = new URLSearchParams(decodedQS)
        const dict: Record<string, string> = {}

        for (const [key, value] of decodedParams.entries()) {
          dict[key] = value
        }

        if (dict['user_id'] && dict['email'] && dict['token']) {
          handleLogOut()
          setSearchData({
            user_id: Number(dict['user_id']),
            token: dict['token'],
            email: dict['email'],
          })
        } else {
          navigate(AppRoutePaths.auth.login)
        }
      } catch (e) {
        navigate(AppRoutePaths.auth.login)
      }
    } else {
      navigate(AppRoutePaths.auth.login)
    }
  }, [encodedQS])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<UserRegistrationFormProps>({
    resolver: zodResolver(UserRegistrationSchema),
  })

  const onSubmit: SubmitHandler<UserRegistrationFormProps> = async (
    formData
  ) => {
    setLoading(true)
    const { error } = await registerUser({
      id: SearchData.user_id,
      token: SearchData.token,
      payload: {
        email: SearchData.email,
        password: formData.password,
        phone: formData.phone,
      },
    })
    setLoading(false)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
      navigate(AppRoutePaths.auth.login)
    } else {
      navigate(AppRoutePaths.auth.login, { state: { signup_success: true } })
    }
  }

  const password = useWatch({ control, name: 'password' })
  const confirmPassword = useWatch({ control, name: 'confirm_password' })

  return (
    <AuthLayout>
      <AuthLayout.Content className="space-y-14">
        <>
          <div>
            <img
              alt="Ace Logo"
              src={AceScholarshipsLogo}
              className="mx-auto h-auto"
              width="90%"
            />
            <p className="mt-3 text-center text-sm text-gray-500">
              Sign Up to Ace Scholarship
            </p>
            <p className="mt-3 text-center text-sm text-gray-500">
              Already have an account?&nbsp;
              <Link
                to={AppRoutePaths.auth.login}
                className="cursor-pointer text-primary font-semibold"
              >
                Log In
              </Link>
            </p>
          </div>
          <form
            noValidate
            className="space-y-6"
            onSubmit={handleSubmit(onSubmit)}
          >
            {loading && (
              <div className="flex flex-col justify-center items-center mt-2">
                <EllipsesLoader size="md" />
              </div>
            )}
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Email
              </label>
              <div className="mt-2">
                <Input
                  {...register('email')}
                  value={SearchData?.email}
                  disabled
                  className="bg-gray-100"
                />
                <FormErrorMessage message={errors.email?.message} />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Password <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative flex">
                <Input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  required
                  placeholder="123456"
                  autoComplete="new-password"
                />
                <div
                  className="absolute right-0 flex items-center h-full mr-2"
                  onClick={() => {
                    setShowPassword(!showPassword)
                  }}
                >
                  {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
                </div>
              </div>
              <FormErrorMessage message={errors.password?.message} />
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative flex">
                <Input
                  {...register('confirm_password')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  placeholder="123456"
                />
                <div
                  className="absolute right-0 flex items-center h-full mr-2"
                  onClick={() => {
                    setShowConfirmPassword(!showConfirmPassword)
                  }}
                >
                  {showConfirmPassword ? <EyeIcon /> : <EyeSlashIcon />}
                </div>
              </div>
              <FormErrorMessage
                message={
                  confirmPassword && password !== confirmPassword
                    ? 'Passwords do not match'
                    : errors.confirm_password?.message
                      ? errors.confirm_password.message
                      : ''
                }
              />
            </div>
            <div>
              <label
                htmlFor="phone-number"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <Input
                  {...register('phone')}
                  type="text"
                  required
                  placeholder="1234567890"
                  maxLength={10}
                  onChange={handlePhoneChange}
                />
                <FormErrorMessage message={errors.phone?.message} />
              </div>
            </div>
            <div className="flex">
              <Button
                type="submit"
                className={cn(
                  'grow',
                  loading
                    ? 'bg-gray-400 hover:bg-gray-400'
                    : 'bg-primary hover:bg-primary'
                )}
              >
                Sign Up
              </Button>
            </div>
          </form>
        </>
      </AuthLayout.Content>
    </AuthLayout>
  )
}

export default SignUpPage
